import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { parentApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Lock, EnvelopeSimple, Globe, User, Phone, Key, Plus, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ParentRegister = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
  });
  // Array so a parent can attach several children at once. Kept as strings, one
  // per input line — dynamically add/remove rows.
  const [studentCodes, setStudentCodes] = useState(['']);
  const [loading, setLoading] = useState(false);
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const setCodeAt = (i, v) => {
    setStudentCodes(prev => prev.map((c, idx) => idx === i ? v : c));
  };
  const addCode = () => setStudentCodes(prev => [...prev, '']);
  const removeCode = (i) => setStudentCodes(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error(language === 'fr' ? 'Le mot de passe doit contenir au moins 6 caractères' : 'Password must be at least 6 characters');
      return;
    }

    const codes = studentCodes.map(c => c.trim()).filter(Boolean);
    if (codes.length === 0) {
      toast.error(language === 'fr' ? 'Au moins un code élève est requis' : 'At least one student code is required');
      return;
    }

    setLoading(true);
    try {
      const { data } = await parentApi.register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        student_codes: codes,
      });
      toast.success(
        language === 'fr'
          ? `Inscription réussie ! ${data.children_count || codes.length} enfant(s) rattaché(s).`
          : `Registration successful! ${data.children_count || codes.length} child(ren) linked.`
      );
      navigate('/parent');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Background */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/568a569c-0fa6-40dc-9d3f-d310e1c010bc/images/4245737ad183253ed165d014c104ffa1ae93f54ac72925dcfa723608d34a2fdb.png)'
        }}
      >
        <div className="absolute inset-0 bg-[#10B981]/70" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/568a569c-0fa6-40dc-9d3f-d310e1c010bc/images/8563b961e801bb242cecdbdafd9d4cf288a10e83064dc5a2edd376c57200f0da.png"
            alt="Logo"
            className="w-24 h-24 mb-8"
          />
          <h1 className="font-outfit text-4xl font-bold mb-4">
            {t('parentPortal')}
          </h1>
          <p className="font-manrope text-lg opacity-90">
            {language === 'fr' 
              ? 'Suivez la scolarité de vos enfants, consultez leurs bulletins et communiquez avec l\'école.'
              : 'Track your children\'s education, view their report cards and communicate with the school.'}
          </p>
        </div>
      </div>
      
      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-[#F8FAFC] p-8">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-8">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleLanguage}
              className="flex items-center gap-2 border-[#E2E8F0] hover:bg-[#F1F5F9]"
            >
              <Globe size={18} />
              {language === 'fr' ? 'EN' : 'FR'}
            </Button>
          </div>
          
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader className="space-y-1">
              <CardTitle className="font-outfit text-2xl text-center text-[#0F172A]">
                {t('parentRegister')}
              </CardTitle>
              <CardDescription className="font-manrope text-center text-[#64748B]">
                {language === 'fr' 
                  ? 'Créez votre compte parent pour accéder au portail'
                  : 'Create your parent account to access the portal'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-manrope">{language === 'fr' ? 'Nom complet' : 'Full Name'}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Jean Dupont"
                      className="pl-10 border-[#E2E8F0]"
                      required
                      data-testid="parent-name-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-manrope">{t('email')}</Label>
                  <div className="relative">
                    <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="parent@email.com"
                      className="pl-10 border-[#E2E8F0]"
                      required
                      data-testid="parent-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone" className="font-manrope">{t('phone')}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+225 00 00 00 00"
                      className="pl-10 border-[#E2E8F0]"
                      data-testid="parent-phone-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="font-manrope">
                    {language === 'fr' ? "Code(s) élève" : "Student code(s)"}
                    <span className="text-[#94A3B8] text-xs ml-2">
                      ({language === 'fr' ? 'un par enfant' : 'one per child'})
                    </span>
                  </Label>
                  <div className="space-y-2" data-testid="student-codes-list">
                    {studentCodes.map((code, i) => (
                      <div className="flex gap-2" key={i}>
                        <div className="relative flex-1">
                          <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
                          <Input
                            value={code}
                            onChange={(e) => setCodeAt(i, e.target.value)}
                            placeholder="STU-XXXXXXXX"
                            className="pl-10 border-[#E2E8F0]"
                            required={i === 0}
                            data-testid={`parent-code-input-${i}`}
                          />
                        </div>
                        {studentCodes.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeCode(i)}
                            className="text-red-600 hover:bg-red-50 shrink-0"
                            aria-label={language === 'fr' ? 'Retirer ce code' : 'Remove this code'}
                            data-testid={`remove-code-${i}`}
                          >
                            <X size={18} />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addCode}
                    className="border-dashed border-[#10B981] text-[#10B981] hover:bg-emerald-50"
                    data-testid="add-code-btn"
                  >
                    <Plus size={14} className="mr-1" />
                    {language === 'fr' ? 'Ajouter un autre enfant' : 'Add another child'}
                  </Button>
                  <p className="text-xs text-[#64748B]">
                    {language === 'fr'
                      ? "Demandez ces codes à l'administration de l'école. Vous pouvez ajouter jusqu'à autant d'enfants que nécessaire."
                      : "Request these codes from the school administration. You can add as many children as needed."}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="font-manrope">{t('password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      className="pl-10 border-[#E2E8F0]"
                      required
                      data-testid="parent-password-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="font-manrope">
                    {language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm Password'}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                      className="pl-10 border-[#E2E8F0]"
                      required
                      data-testid="parent-confirm-password-input"
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-manrope"
                  disabled={loading}
                  data-testid="parent-register-submit"
                >
                  {loading ? t('loading') : t('registerAsParent')}
                </Button>
              </form>
              
              <p className="mt-4 text-center text-sm text-[#64748B] font-manrope">
                {t('alreadyHaveAccount')}{' '}
                <Link to="/login" className="text-[#10B981] hover:underline">
                  {t('login')}
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ParentRegister;
